import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV55TransitionParams {
  /**
   * 视频生成提示词
   */
  prompt: string;
  /**
   * 首帧图片URL或Base64数据(兼容字段),会自动上传至PixVerse并转换为img_id
   */
  first_frame_img?: string;
  /**
   * 首帧图片URL或Base64数据,会自动上传至PixVerse并转换为img_id
   */
  first_frame_image: string;
  /**
   * 尾帧图片URL或Base64数据(兼容字段),会自动上传至PixVerse并转换为img_id
   */
  last_frame_img?: string;
  /**
   * 尾帧图片URL或Base64数据,会自动上传至PixVerse并转换为img_id
   */
  last_frame_image: string;
  /**
   * 视频时长(秒)，1080p不能使用10秒
   * @values 5 | 8 | 10
   */
  duration: 5 | 8 | 10;
  /**
   * 视频质量，Turbo可以使用360p
   * @values 360p | 540p | 720p | 1080p
   */
  quality: '360p' | '540p' | '720p' | '1080p';
  /**
   * 随机种子,用于生成可复现的结果(可选)
   * @min 0
   * @max 2147483647
   */
  seed?: number;
  /**
   * 音效内容描述
   */
  sound_effect_content?: string;
  /**
   * 口型同步TTS文本内容
   */
  lip_sync_tts_content?: string;
  /**
   * 口型同步TTS说话人ID
   */
  lip_sync_tts_speaker_id?: string;
  /**
   * 是否生成音频开关(V5.5特有)
   */
  generate_audio_switch?: boolean;
}

export interface PixverseV55TransitionResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v5_5_transition
 *
 * <p>PixVerse V5.5 首尾帧过渡视频生成接口，通过提供首帧和尾帧图片生成过渡视频</p>
 * <p>API Documentation: https://docs.platform.pixverse.ai/transitionfirst-last-frame-generation-15123014e0</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV55Transition } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV55Transition({
 *   // 参数见 PixverseV55TransitionParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV55TransitionParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV55Transition(
  params: PixverseV55TransitionParams,
): Promise<PixverseV55TransitionResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v5_5_transition',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV55TransitionResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
